import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { MainLayout } from "@/components/layout/MainLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Loader2, Upload, Palette, Type, Mail, FileText, Link } from "lucide-react";
import { LogoUploader } from "@/components/agency/branding/LogoUploader";
import { ColorPicker } from "@/components/agency/branding/ColorPicker";
import { BrandingPreview } from "@/components/agency/branding/BrandingPreview";

const FONT_OPTIONS = [
  "Inter", "Roboto", "Open Sans", "Lato", "Poppins",
  "Montserrat", "Source Sans Pro", "Nunito", "Raleway", "Work Sans",
];

interface BrandingConfig {
  logo_url: string | null;
  logo_dark_url: string | null;
  favicon_url: string | null;
  primary_color: string;
  secondary_color: string;
  font_family: string;
  email_from_name: string | null;
  email_from_address: string | null;
  terms_url: string | null;
  privacy_url: string | null;
  support_email: string | null;
  login_message: string | null;
  dashboard_footer: string | null;
  powered_by_text: string | null;
  allow_tenant_branding: boolean;
}

const DEFAULT_BRANDING: BrandingConfig = {
  logo_url: null,
  logo_dark_url: null,
  favicon_url: null,
  primary_color: "#6366f1",
  secondary_color: "#8b5cf6",
  font_family: "Inter",
  email_from_name: null,
  email_from_address: null,
  terms_url: null,
  privacy_url: null,
  support_email: null,
  login_message: null,
  dashboard_footer: null,
  powered_by_text: null,
  allow_tenant_branding: false,
};

export default function BrandingPage() {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<BrandingConfig>(DEFAULT_BRANDING);
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch branding
  const { data: brandingResponse, isLoading, error } = useQuery({
    queryKey: ["agency-branding"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("Calie_getBranding", {
        body: {},
      });
      if (error) throw error;
      return data;
    },
  });

  // Initialize form when data loads
  useEffect(() => {
    if (brandingResponse?.branding) {
      setFormData(brandingResponse.branding);
      setHasChanges(false);
    }
  }, [brandingResponse]);

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: Partial<BrandingConfig>) => {
      const { data: response, error } = await supabase.functions.invoke("Calie_updateBranding", {
        body: data,
      });
      if (error) throw error;
      return response;
    },
    onSuccess: () => {
      toast.success("Branding updated successfully");
      queryClient.invalidateQueries({ queryKey: ["agency-branding"] });
      setHasChanges(false);
    },
    onError: (error: Error) => {
      toast.error(`Failed to update branding: ${error.message}`);
    },
  });

  const handleChange = (field: keyof BrandingConfig, value: string | boolean | null) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleLogoUpload = (field: "logo_url" | "logo_dark_url" | "favicon_url", url: string) => {
    setFormData((prev) => ({ ...prev, [field]: url }));
    setHasChanges(true);
  };

  const handleSave = () => {
    updateMutation.mutate(formData);
  };

  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <div className="text-center py-8">
          <p className="text-destructive">Failed to load branding settings</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="container max-w-7xl mx-auto px-4 py-6 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Agency Branding</h1>
            <p className="text-muted-foreground">Customize your agency's appearance</p>
          </div>
          <Button 
            onClick={handleSave} 
            disabled={!hasChanges || updateMutation.isPending}
          >
            {updateMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save Changes
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Settings Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Logo Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="h-5 w-5" />
                  Logos & Favicon
                </CardTitle>
                <CardDescription>Upload your agency logos (max 500KB each)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <LogoUploader
                    label="Main Logo"
                    assetType="logo"
                    currentUrl={formData.logo_url}
                    onUpload={(url) => handleLogoUpload("logo_url", url)}
                  />
                  <LogoUploader
                    label="Dark Mode Logo"
                    assetType="logo_dark"
                    currentUrl={formData.logo_dark_url}
                    onUpload={(url) => handleLogoUpload("logo_dark_url", url)}
                  />
                  <LogoUploader
                    label="Favicon"
                    assetType="favicon"
                    currentUrl={formData.favicon_url}
                    onUpload={(url) => handleLogoUpload("favicon_url", url)}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Colors & Typography */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Palette className="h-5 w-5" />
                  Colors & Typography
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <ColorPicker
                    label="Primary Color"
                    value={formData.primary_color}
                    onChange={(color) => handleChange("primary_color", color)}
                  />
                  <ColorPicker
                    label="Secondary Color"
                    value={formData.secondary_color}
                    onChange={(color) => handleChange("secondary_color", color)}
                  />
                </div>
                <div>
                  <Label>Font Family</Label>
                  <Select
                    value={formData.font_family}
                    onValueChange={(value) => handleChange("font_family", value)}
                  >
                    <SelectTrigger className="mt-1.5">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FONT_OPTIONS.map((font) => (
                        <SelectItem key={font} value={font} style={{ fontFamily: font }}>
                          {font}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Email Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  Email Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email_from_name">From Name</Label>
                    <Input
                      id="email_from_name"
                      value={formData.email_from_name || ""}
                      onChange={(e) => handleChange("email_from_name", e.target.value || null)}
                      placeholder="Your Agency Name"
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email_from_address">From Email</Label>
                    <Input
                      id="email_from_address"
                      type="email"
                      value={formData.email_from_address || ""}
                      onChange={(e) => handleChange("email_from_address", e.target.value || null)}
                      placeholder="noreply@youragency.com"
                      className="mt-1.5"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="support_email">Support Email</Label>
                  <Input
                    id="support_email"
                    type="email"
                    value={formData.support_email || ""}
                    onChange={(e) => handleChange("support_email", e.target.value || null)}
                    placeholder="support@youragency.com"
                    className="mt-1.5"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Legal & Links */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Link className="h-5 w-5" />
                  Legal & Links
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="terms_url">Terms of Service URL</Label>
                    <Input
                      id="terms_url"
                      type="url"
                      value={formData.terms_url || ""}
                      onChange={(e) => handleChange("terms_url", e.target.value || null)}
                      placeholder="https://youragency.com/terms"
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="privacy_url">Privacy Policy URL</Label>
                    <Input
                      id="privacy_url"
                      type="url"
                      value={formData.privacy_url || ""}
                      onChange={(e) => handleChange("privacy_url", e.target.value || null)}
                      placeholder="https://youragency.com/privacy"
                      className="mt-1.5"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Custom Text */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Custom Text
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="login_message">Login Page Message</Label>
                  <Textarea
                    id="login_message"
                    value={formData.login_message || ""}
                    onChange={(e) => handleChange("login_message", e.target.value || null)}
                    placeholder="Welcome back! Sign in to access your dashboard."
                    className="mt-1.5"
                    rows={2}
                  />
                </div>
                <div>
                  <Label htmlFor="dashboard_footer">Dashboard Footer</Label>
                  <Input
                    id="dashboard_footer"
                    value={formData.dashboard_footer || ""}
                    onChange={(e) => handleChange("dashboard_footer", e.target.value || null)}
                    placeholder="© 2024 Your Agency. All rights reserved."
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="powered_by_text">Powered By Text</Label>
                  <Input
                    id="powered_by_text"
                    value={formData.powered_by_text || ""}
                    onChange={(e) => handleChange("powered_by_text", e.target.value || null)}
                    placeholder="Powered by Calie.ai"
                    className="mt-1.5"
                  />
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Allow Tenant Branding</Label>
                    <p className="text-sm text-muted-foreground">
                      Let your clients customize their own branding
                    </p>
                  </div>
                  <Switch
                    checked={formData.allow_tenant_branding}
                    onCheckedChange={(checked) => handleChange("allow_tenant_branding", checked)}
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Preview Column */}
          <div className="lg:col-span-1">
            <div className="sticky top-6">
              <BrandingPreview branding={formData} />
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
